import { Alert, FormControl, FormErrorMessage, Radio, RadioGroup, Stack, Text } from '@chakra-ui/react'
import type { InsuranceParamsPropertyDto, Property } from 'BrokerTsLib/src/dtos'
import { Product, RiskVariant, Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'

import { Datepicker } from '@/components/Datepicker'
import { FormGroup } from '@/components/FormGroup'
import { FormLabel } from '@/components/FormLabel'
import { FormSection, FormSectionBody, FormSectionDescription, FormSectionHeader, FormSectionTitle } from '@/components/FormSection'
import { FormSpacer } from '@/components/FormSpacer'
import { InputGroup } from '@/components/InputGroup'
import { InsuranceCompanies } from '@/components/InsuranceCompanies'
import { RadioStack } from '@/components/RadioStack'
import { ContractReplacement } from '@/containers/carkulka/ContractReplacement'
import { useInsurers, useSelections } from '@/hooks'
import { useApp } from '@/hooks/useApp'
import { useNameof } from '@/hooks/useNameof'
import { useGetStep1PropertyWarnings } from '@/hooks/useWizard'

import { PropertyInsuranceTypeSelector } from '../PropertyInsuranceTypeSelector'

export function BaseParametersForm() {
  const nameof = useNameof<InsuranceParamsPropertyDto>()
  const pnameof = useNameof<Property>(nameof((x) => x.property))

  const { product } = useApp()

  const { data: dataPf } = useSelections(SelectionType.FrekvencePlatby)
  const { data: dataPt } = useSelections(SelectionType.ZpusobPlatby)

  const { data: allInsurers, isLoading: isLoadingInsurers, isFetching: isFetchingInsurers } = useInsurers(false, null)

  const methods = useFormContext<InsuranceParamsPropertyDto>()
  const {
    register,
    setValue,
    watch,
    getValues,
    formState: { isSubmitting, errors }
  } = methods

  const { data: dataWarnings } = useGetStep1PropertyWarnings()

  const isContracted = !!getValues('generalParams.contractNumber')
  const onlyLotNumberValue = watch('property.data.address.isCatastralNumber')
  const selectedRealtyType = watch('property.data.realtyTypeId')

  const warnings = dataWarnings?.data?.filter(
    (x) =>
      x.isAddressLocationCadastral == onlyLotNumberValue &&
      !!selectedRealtyType &&
      x.isRecreationalProperty ==
      (selectedRealtyType == Selection.RekreacniObjekt),
  ) ?? []

  const minStartDate = new Date().customAddDays(1).setHours(0, 0, 0, 0)

  return (
    <FormSection>
      <FormSectionHeader>
        <FormSectionTitle>Parametry pojištění</FormSectionTitle>

        <FormSectionDescription>
          Zadejte základní parametry pojištění.
        </FormSectionDescription>
      </FormSectionHeader>

      <FormSectionBody>
        {
          /* Project name */
        }
        <FormControl isInvalid={!!errors.generalParams?.name}>
          <FormLabel tooltip="Zadejte název nabídky" htmlFor="projectName">
            Název nabídky
          </FormLabel>
          <InputGroup id="projectName" placeholder="Zadejte název nabídky" htmlSize={32} isDisabled={isContracted} errors={errors.generalParams?.name} {...register('generalParams.name', {// required: 'Zadejte název nabídky',
          })} />
        </FormControl>

        <FormSpacer />

        {product == Product.PropertyInsurance && <ContractReplacement />}

        <FormSpacer />

        <PropertyInsuranceTypeSelector />

        <FormSpacer />

        {
          /* Date */
        }
        <FormControl isInvalid={!!errors.generalParams?.insuranceStartDate}>
          <FormLabel htmlFor="startDate" tooltip={<>
            Počátek je možné nastavit nejdříve na následující den.
            <br />
            Allianz pojišťovna umožňuje sjednat pojištění s odloženým
            počátkem až o 1 rok.
            <br />
            Česká podnikatelská pojišťovna umožňuje sjednat pojištění
            s odloženým počátkem až o 1 rok.
            <br />
            ČSOB pojišťovna umožňuje sjednat pojištění až maximálně 3
            měsíce dopředu.
            <br />
            Direct pojišťovna umožňuje sjednat pojištění až maximálně
            6 měsíců dopředu.
            <br />
            Kooperativa pojišťovna umožňuje sjednat pojištění až
            maximálně 1 rok dopředu.
            <br />
            Pillow pojišťovna umožňuje sjednat pojištění až maximálně
            3 měsíce dopředu.
            <br />
            Slavia pojišťovna umožňuje sjednat pojištění až maximálně
            9 měsíců dopředu.
            <br />
            UNIQA pojišťovna umožňuje sjednat pojištění až maximálně 1
            rok dopředu.
            <br />
            Generali Česká pojišťovna umožňuje sjednat pojištění až
            maximálně 9 měsíců dopředu.
            <br />
          </>}>
            Datum počátku pojištění
          </FormLabel>

          <Datepicker isDisabled={isContracted} errors={errors.generalParams?.insuranceStartDate} date={getValues('generalParams.insuranceStartDate')} onDateChange={date => setValue('generalParams.insuranceStartDate', date)} options={{
            minDate: minStartDate
          }} />
        </FormControl>

        <FormSpacer />

        <FormGroup size="lg">
          <FormControl isInvalid={!!errors.generalParams?.paymentFrequency}>
            <FormLabel htmlFor="paymentFrequency" tooltip={<>
              Vyberte frekvenci plateb
              <br />
              Allianz umožňuje: roční, měsíční
              <br />
              ČPP umožňuje: roční, pololetní, čtvrtletní
              <br />
              ČSOB umožňuje: roční, pololetní, čtvrtletní
              <br />
              Direct umožňuje: roční, pololetní, čtvrtletní
              <br />
              GČP umožňuje: roční, pololetní, čtvrtletní, měsíční
              <br />
              Kooperativa umožňuje: roční, pololetní, čtvrtletní,
              měsíční
              <br />
              Pillow umožňuje: roční, pololetní, čtvrtletní, měsíční
              <br />
              Slavia umožňuje: roční, pololetní, čtvrtletní
              <br />
              UNIQA umožňuje: roční, pololetní, čtvrtletní
            </>}>
              Frekvence plateb
            </FormLabel>

            <RadioGroup isDisabled={isContracted} onChange={x => setValue('generalParams.paymentFrequency', parseInt(x))} value={watch('generalParams.paymentFrequency')?.toString()}>
              <Stack direction="column" spacing={4}>
                {dataPf?.filter(item => item.id != Selection.OneTime).map(item => <Radio value={item.id.toString()} key={item.id.toString()}>
                  {item.description}
                </Radio>)}
              </Stack>
            </RadioGroup>

            {errors.generalParams?.paymentFrequency && <FormErrorMessage>
              {errors.generalParams?.paymentFrequency.message}
            </FormErrorMessage>}
          </FormControl>

          <FormControl isInvalid={!!errors.generalParams?.paymentType}>
            <FormLabel htmlFor="paymentMethod">Způsob platby</FormLabel>

            <RadioGroup isDisabled={isContracted} onChange={x => setValue('generalParams.paymentType', parseInt(x))} value={watch('generalParams.paymentType')?.toString()}>
              <Stack direction="column" spacing={4}>
                {dataPt?.map(item => <Radio value={item.id.toString()} key={item.id.toString()}>
                  {item.description}
                </Radio>)}
              </Stack>
            </RadioGroup>

            {errors.generalParams?.paymentType && <FormErrorMessage>
              {errors.generalParams?.paymentType.message}
            </FormErrorMessage>}
          </FormControl>
        </FormGroup>

        {product == Product.PropertyInsurance && <>
          <FormSpacer />

          <FormControl isInvalid={!!errors.property?.range}>
            <FormLabel>Rozsah pojištění</FormLabel>

            <RadioGroup onChange={x => setValue('property.range', parseInt(x))} value={watch('property.range')?.toString()} isDisabled={isContracted}>
              <RadioStack variant="stackedTile">
                <Radio value={RiskVariant.MiniRange.toString()}>
                  Nižší
                </Radio>
                <Radio value={RiskVariant.MaxiRange.toString()}>
                  Vyšší
                </Radio>
              </RadioStack>
            </RadioGroup>

            {errors.property?.range && <FormErrorMessage>
              {errors.property?.range.message}
            </FormErrorMessage>}
          </FormControl>
        </>
        }

        <FormSpacer />

        <FormControl isInvalid={!!errors.property?.insurers}>
          <InsuranceCompanies
            companies={allInsurers ?? []}
            isLoadingCompanies={isFetchingInsurers || isLoadingInsurers}
            propPath={pnameof(x => x.insurers)}
            isDisabled={isContracted}
            tooltip="Vyberte pojišťovny, které chcete zahrnout do kalkulace pojištění." />

          {errors.property?.insurers && <FormErrorMessage>
            {errors.property.insurers.message}
          </FormErrorMessage>}
        </FormControl>

        {warnings.length > 0 && <Alert status='warning'>
          <Stack direction="column">
            {warnings.map(item => {
              return <Text key={item.message} dangerouslySetInnerHTML={{
                __html: item.message
              }} />
            })}
          </Stack>
        </Alert>}
      </FormSectionBody>
    </FormSection>)
}